import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Lock,
  Plus,
  Trash2,
  Save,
  Clock,
  Tags,
  Flag,
  Truck,
  Layers,
  Loader2,
  Archive,
  Bell,
  CalendarClock,
} from "lucide-react";
import { toast } from "sonner";
import TicketNotificationsPanel from "@/components/TicketNotificationsPanel";
import ScheduledTicketsPanel from "@/components/ScheduledTicketsPanel";

const PALETTES = ["blue", "violet", "amber", "orange", "emerald", "red", "slate", "rose", "sky", "lime"];
const PALETTE_CLASS = {
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  violet: "bg-violet-50 text-violet-700 border-violet-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  orange: "bg-orange-50 text-orange-700 border-orange-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  red: "bg-red-50 text-red-700 border-red-200",
  slate: "bg-slate-50 text-slate-600 border-slate-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  sky: "bg-sky-50 text-sky-700 border-sky-200",
  lime: "bg-lime-50 text-lime-700 border-lime-200",
};

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function SupportConfig() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("statuses");

  if (!isAdmin) {
    return (
      <Card className="p-12 text-center border-slate-200" data-testid="support-config-forbidden">
        <Lock className="h-10 w-10 text-slate-300 mx-auto mb-3" />
        <div className="font-heading text-lg text-slate-700">Admin access required</div>
        <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
          Only admins can change support configuration. Ask an existing admin to
          change your role from <span className="font-mono text-slate-700">{user?.role || "user"}</span>.
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="support-config-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Service Desk</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Support configuration</h1>
        <p className="text-slate-500 text-sm mt-1 max-w-2xl">
          Customise ticket statuses, priorities, SLA targets, vendors and products. Changes apply
          immediately across the Tickets module.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent border-b border-slate-200 rounded-none w-full justify-start h-auto p-0 gap-1">
          {[
            ["statuses", "Statuses", Tags],
            ["priorities", "Priorities", Flag],
            ["sla", "SLA & hours", Clock],
            ["alerts", "Alerts", Bell],
            ["scheduled", "Scheduled tickets", CalendarClock],
            ["vendors", "Vendors", Truck],
            ["products", "Products", Layers],
          ].map(([v, l, Icon]) => (
            <TabsTrigger
              key={v}
              value={v}
              data-testid={`config-tab-${v}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-700 data-[state=active]:shadow-none px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-900 flex items-center gap-2"
            >
              <Icon className="h-3.5 w-3.5" />
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="statuses" className="mt-6"><StatusesPanel /></TabsContent>
        <TabsContent value="priorities" className="mt-6"><PrioritiesPanel /></TabsContent>
        <TabsContent value="sla" className="mt-6"><SlaPanel /></TabsContent>
        <TabsContent value="alerts" className="mt-6"><TicketNotificationsPanel /></TabsContent>
        <TabsContent value="scheduled" className="mt-6"><ScheduledTicketsPanel /></TabsContent>
        <TabsContent value="vendors" className="mt-6"><VendorsPanel /></TabsContent>
        <TabsContent value="products" className="mt-6"><ProductsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------- Statuses ----------------
function StatusesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", color: "blue", is_closed: false });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/config/ticket-statuses?include_archived=true");
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      await api.post("/config/ticket-statuses", form);
      setForm({ name: "", color: "blue", is_closed: false });
      toast.success("Status added");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const update = async (id, patch) => {
    try {
      await api.patch(`/config/ticket-statuses/${id}`, patch);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete status "${name}"? Tickets currently using it will keep the label, but it'll be archived instead of removed.`)) return;
    try {
      const r = await api.delete(`/config/ticket-statuses/${id}`);
      if (r.data?.archived) toast.success(`Archived — ${r.data.tickets_using} ticket(s) still use it.`);
      else toast.success("Status removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Card className="border-slate-200" data-testid="statuses-panel">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <Tags className="h-4 w-4 text-blue-600" /> Ticket statuses
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Statuses appear in the Status dropdown when creating or editing a ticket. Mark
          "Closes ticket" to count it in the Resolved/Closed bucket for stats.
        </p>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        {/* Add form */}
        <div className="grid grid-cols-12 gap-2 items-end p-3 border border-dashed border-slate-300 rounded-md bg-slate-50/40">
          <div className="col-span-5">
            <Label className="text-xs">Name</Label>
            <Input
              data-testid="new-status-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Awaiting Vendor"
            />
          </div>
          <div className="col-span-3">
            <Label className="text-xs">Colour</Label>
            <Select value={form.color} onValueChange={(v) => setForm({ ...form, color: v })}>
              <SelectTrigger data-testid="new-status-color"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PALETTES.map((c) => (
                  <SelectItem key={c} value={c}>
                    <span className={`px-2 py-0.5 rounded text-[11px] font-semibold border ${PALETTE_CLASS[c]}`}>{c}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-3 flex items-center gap-2 pb-2">
            <Switch
              id="new-status-closed"
              checked={form.is_closed}
              onCheckedChange={(v) => setForm({ ...form, is_closed: v })}
              data-testid="new-status-closed"
            />
            <Label htmlFor="new-status-closed" className="text-xs cursor-pointer">Closes ticket</Label>
          </div>
          <div className="col-span-1 flex justify-end">
            <Button onClick={add} data-testid="add-status-btn" size="sm">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-xs text-slate-400 py-4">Loading…</div>
        ) : (
          <table className="w-full text-sm" data-testid="statuses-table">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="py-2 pl-1">Name</th>
                <th className="py-2 w-32">Colour</th>
                <th className="py-2 w-32">Closes ticket</th>
                <th className="py-2 w-24">Archived</th>
                <th className="py-2 w-16 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} data-testid={`status-row-${s.id}`} className="border-b border-slate-50">
                  <td className="py-2 pl-1">
                    <Input
                      defaultValue={s.name}
                      onBlur={(e) => e.target.value !== s.name && update(s.id, { name: e.target.value })}
                      className="h-8"
                    />
                  </td>
                  <td className="py-2">
                    <Select value={s.color || "slate"} onValueChange={(v) => update(s.id, { color: v })}>
                      <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PALETTES.map((c) => (
                          <SelectItem key={c} value={c}>
                            <span className={`px-2 py-0.5 rounded text-[11px] font-semibold border ${PALETTE_CLASS[c]}`}>{c}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-2">
                    <Switch
                      checked={!!s.is_closed}
                      onCheckedChange={(v) => update(s.id, { is_closed: v })}
                    />
                  </td>
                  <td className="py-2">
                    <Switch
                      checked={!!s.archived}
                      onCheckedChange={(v) => update(s.id, { archived: v })}
                    />
                  </td>
                  <td className="py-2 text-right">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => remove(s.id, s.name)}
                      data-testid={`delete-status-${s.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-xs text-slate-400">No statuses configured</td></tr>
              )}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------- Priorities ----------------
function PrioritiesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", color: "blue", p_level: 3 });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/config/ticket-priorities?include_archived=true");
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      await api.post("/config/ticket-priorities", form);
      setForm({ name: "", color: "blue", p_level: 3 });
      toast.success("Priority added");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const update = async (id, patch) => {
    try { await api.patch(`/config/ticket-priorities/${id}`, patch); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete priority "${name}"?`)) return;
    try {
      const r = await api.delete(`/config/ticket-priorities/${id}`);
      if (r.data?.archived) toast.success(`Archived — ${r.data.tickets_using} ticket(s) still use it.`);
      else toast.success("Priority removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Card className="border-slate-200" data-testid="priorities-panel">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <Flag className="h-4 w-4 text-blue-600" /> Ticket priorities
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Each priority maps to a <span className="font-mono">P-level (1–4)</span> used by the SLA matrix.
          P1 is highest urgency.
        </p>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <div className="grid grid-cols-12 gap-2 items-end p-3 border border-dashed border-slate-300 rounded-md bg-slate-50/40">
          <div className="col-span-4">
            <Label className="text-xs">Name</Label>
            <Input
              data-testid="new-priority-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Critical"
            />
          </div>
          <div className="col-span-3">
            <Label className="text-xs">Colour</Label>
            <Select value={form.color} onValueChange={(v) => setForm({ ...form, color: v })}>
              <SelectTrigger data-testid="new-priority-color"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PALETTES.map((c) => (
                  <SelectItem key={c} value={c}>
                    <span className={`px-2 py-0.5 rounded text-[11px] font-semibold border ${PALETTE_CLASS[c]}`}>{c}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-4">
            <Label className="text-xs">P-Level</Label>
            <Select value={String(form.p_level)} onValueChange={(v) => setForm({ ...form, p_level: Number(v) })}>
              <SelectTrigger data-testid="new-priority-plevel"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">P1 — highest urgency</SelectItem>
                <SelectItem value="2">P2</SelectItem>
                <SelectItem value="3">P3</SelectItem>
                <SelectItem value="4">P4 — lowest urgency</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-1 flex justify-end">
            <Button onClick={add} data-testid="add-priority-btn" size="sm">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-xs text-slate-400 py-4">Loading…</div>
        ) : (
          <table className="w-full text-sm" data-testid="priorities-table">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="py-2 pl-1">Name</th>
                <th className="py-2 w-32">Colour</th>
                <th className="py-2 w-32">P-Level</th>
                <th className="py-2 w-24">Archived</th>
                <th className="py-2 w-16 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} data-testid={`priority-row-${p.id}`} className="border-b border-slate-50">
                  <td className="py-2 pl-1">
                    <Input
                      defaultValue={p.name}
                      onBlur={(e) => e.target.value !== p.name && update(p.id, { name: e.target.value })}
                      className="h-8"
                    />
                  </td>
                  <td className="py-2">
                    <Select value={p.color || "slate"} onValueChange={(v) => update(p.id, { color: v })}>
                      <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PALETTES.map((c) => (
                          <SelectItem key={c} value={c}>
                            <span className={`px-2 py-0.5 rounded text-[11px] font-semibold border ${PALETTE_CLASS[c]}`}>{c}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-2">
                    <Select value={String(p.p_level)} onValueChange={(v) => update(p.id, { p_level: Number(v) })}>
                      <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4].map((n) => <SelectItem key={n} value={String(n)}>P{n}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-2">
                    <Switch checked={!!p.archived} onCheckedChange={(v) => update(p.id, { archived: v })} />
                  </td>
                  <td className="py-2 text-right">
                    <Button size="icon" variant="ghost" onClick={() => remove(p.id, p.name)} data-testid={`delete-priority-${p.id}`}>
                      <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-xs text-slate-400">No priorities configured</td></tr>
              )}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------- SLA ----------------
function SlaPanel() {
  const [sla, setSla] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const r = await api.get("/config/sla"); setSla(r.data); }
    catch (e) { toast.error("Failed to load SLA"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/config/sla", sla);
      toast.success("SLA saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  if (!sla) return <div className="text-center text-xs text-slate-400 py-6">Loading…</div>;

  const updateRow = (p_level, patch) =>
    setSla({ ...sla, rows: sla.rows.map((r) => r.p_level === p_level ? { ...r, ...patch } : r) });
  const updateDay = (day, patch) =>
    setSla({ ...sla, working_days: sla.working_days.map((d) => d.day === day ? { ...d, ...patch } : d) });

  return (
    <div className="space-y-5" data-testid="sla-panel">
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Clock className="h-4 w-4 text-blue-600" /> SLA targets (per priority)
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Time-to-first-response and time-to-resolution in <span className="font-mono">minutes</span>. Use whole numbers.
            Common conversions: <span className="font-mono">60</span>=1h · <span className="font-mono">240</span>=4h · <span className="font-mono">480</span>=8h · <span className="font-mono">1440</span>=24h · <span className="font-mono">2880</span>=48h.
          </p>
        </CardHeader>
        <CardContent className="p-5">
          <table className="w-full text-sm" data-testid="sla-table">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="py-2 pl-1 w-24">Priority</th>
                <th className="py-2">Respond within (mins)</th>
                <th className="py-2">Resolve within (mins)</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4].map((pl) => {
                const row = sla.rows.find((r) => r.p_level === pl) || { p_level: pl, respond_within_minutes: 0, resolve_within_minutes: 0 };
                return (
                  <tr key={pl} data-testid={`sla-row-p${pl}`} className="border-b border-slate-50">
                    <td className="py-2 pl-1 font-mono text-slate-700">P{pl}</td>
                    <td className="py-2">
                      <Input
                        type="number"
                        min={0}
                        value={row.respond_within_minutes}
                        onChange={(e) => updateRow(pl, { respond_within_minutes: Number(e.target.value || 0) })}
                        data-testid={`sla-respond-p${pl}`}
                        className="h-8 max-w-[160px]"
                      />
                    </td>
                    <td className="py-2">
                      <Input
                        type="number"
                        min={0}
                        value={row.resolve_within_minutes}
                        onChange={(e) => updateRow(pl, { resolve_within_minutes: Number(e.target.value || 0) })}
                        data-testid={`sla-resolve-p${pl}`}
                        className="h-8 max-w-[160px]"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="mt-5 pt-4 border-t border-slate-100 flex items-start gap-3">
            <div className="flex-1">
              <Label className="text-sm font-medium text-slate-800">Warn before SLA deadline</Label>
              <p className="text-xs text-slate-500 mt-0.5">
                Send the <span className="font-medium">"SLA breaching"</span> alert this many minutes before a ticket's resolution deadline. Set <span className="font-mono">0</span> to disable the pre-deadline warning (the breach alert still fires). Configure who receives it under Admin → Integrations → Ticket alerts.
              </p>
            </div>
            <Input
              type="number"
              min={0}
              value={sla.warn_before_minutes ?? 60}
              onChange={(e) => setSla({ ...sla, warn_before_minutes: Number(e.target.value || 0) })}
              data-testid="sla-warn-before-input"
              className="h-8 w-28"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Clock className="h-4 w-4 text-blue-600" /> Working hours
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            SLA clocks pause outside working hours if enabled below. Times are 24-hour
            in <span className="font-mono">{sla.timezone}</span>.
          </p>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-3 p-3 border border-slate-200 rounded-md bg-slate-50/40">
            <Switch
              id="pause-outside"
              checked={!!sla.pause_outside_hours}
              onCheckedChange={(v) => setSla({ ...sla, pause_outside_hours: v })}
              data-testid="sla-pause-toggle"
            />
            <Label htmlFor="pause-outside" className="text-sm cursor-pointer">
              Pause SLA clocks outside working hours
            </Label>
          </div>

          <table className="w-full text-sm" data-testid="working-hours-table">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="py-2 pl-1 w-24">Day</th>
                <th className="py-2 w-24">Enabled</th>
                <th className="py-2">Start</th>
                <th className="py-2">End</th>
              </tr>
            </thead>
            <tbody>
              {[0, 1, 2, 3, 4, 5, 6].map((d) => {
                const row = sla.working_days.find((x) => x.day === d) || { day: d, enabled: false, start: "09:00", end: "17:30" };
                return (
                  <tr key={d} data-testid={`wh-row-${d}`} className="border-b border-slate-50">
                    <td className="py-2 pl-1 font-mono text-slate-700">{WEEKDAYS[d]}</td>
                    <td className="py-2">
                      <Switch
                        checked={!!row.enabled}
                        onCheckedChange={(v) => updateDay(d, { enabled: v })}
                        data-testid={`wh-enabled-${d}`}
                      />
                    </td>
                    <td className="py-2">
                      <Input
                        type="time"
                        value={row.start}
                        onChange={(e) => updateDay(d, { start: e.target.value })}
                        className="h-8 max-w-[140px]"
                        disabled={!row.enabled}
                        data-testid={`wh-start-${d}`}
                      />
                    </td>
                    <td className="py-2">
                      <Input
                        type="time"
                        value={row.end}
                        onChange={(e) => updateDay(d, { end: e.target.value })}
                        className="h-8 max-w-[140px]"
                        disabled={!row.enabled}
                        data-testid={`wh-end-${d}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="save-sla-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save SLA
        </Button>
      </div>
    </div>
  );
}

// ---------------- Vendors ----------------
function VendorsPanel() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", website: "" });

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/vendors"); setVendors(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name.trim()) return toast.error("Vendor name required");
    try {
      await api.post("/vendors", form);
      setForm({ name: "", website: "" });
      toast.success("Vendor added");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const archive = async (id, name) => {
    if (!window.confirm(`Archive vendor "${name}"?`)) return;
    try { await api.delete(`/vendors/${id}`); toast.success("Vendor archived"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Card className="border-slate-200" data-testid="vendors-panel">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <Truck className="h-4 w-4 text-blue-600" /> Vendors
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Vendors selectable on a ticket. For full contacts &amp; product pricing, manage them
          in <a className="text-blue-600 hover:underline" href="/app/vendors">Operations → Vendors</a>.
        </p>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <div className="grid grid-cols-12 gap-2 items-end p-3 border border-dashed border-slate-300 rounded-md bg-slate-50/40">
          <div className="col-span-6">
            <Label className="text-xs">Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Mimecast"
              data-testid="new-vendor-name"
            />
          </div>
          <div className="col-span-5">
            <Label className="text-xs">Website</Label>
            <Input
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="https://"
              data-testid="new-vendor-website"
            />
          </div>
          <div className="col-span-1 flex justify-end">
            <Button onClick={add} data-testid="add-vendor-btn" size="sm">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-xs text-slate-400 py-4">Loading…</div>
        ) : (
          <table className="w-full text-sm" data-testid="vendors-table">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="py-2 pl-1">Name</th>
                <th className="py-2">Website</th>
                <th className="py-2 w-24">Archived</th>
                <th className="py-2 w-16 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((v) => (
                <tr key={v.id} data-testid={`vendor-row-${v.id}`} className="border-b border-slate-50">
                  <td className="py-2 pl-1 text-slate-800 font-medium">{v.name}</td>
                  <td className="py-2 text-slate-600">{v.website ? <a className="text-blue-600 hover:underline" href={v.website} target="_blank" rel="noreferrer">{v.website}</a> : "—"}</td>
                  <td className="py-2">
                    {v.archived ? <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">archived</span> : <span className="text-[11px] text-slate-400">—</span>}
                  </td>
                  <td className="py-2 text-right">
                    {!v.archived && (
                      <Button size="icon" variant="ghost" onClick={() => archive(v.id, v.name)} data-testid={`archive-vendor-${v.id}`}>
                        <Archive className="h-4 w-4 text-slate-400 hover:text-amber-600" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {vendors.length === 0 && (
                <tr><td colSpan={4} className="py-6 text-center text-xs text-slate-400">No vendors yet</td></tr>
              )}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------- Products ----------------
function ProductsPanel() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", category: "Cyber Security", description: "" });

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/stax-products"); setProducts(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name.trim()) return toast.error("Product name required");
    try {
      await api.post("/stax-products", form);
      setForm({ name: "", category: "Cyber Security", description: "" });
      toast.success("Product added");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const update = async (id, patch) => {
    try { await api.patch(`/stax-products/${id}`, patch); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const archive = async (id, name) => {
    if (!window.confirm(`Archive product "${name}"?`)) return;
    try { await api.delete(`/stax-products/${id}`); toast.success("Product archived"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const categories = useMemo(() => {
    const set = new Set(["Cyber Security", "Safeguarding", "Custom"]);
    products.forEach((p) => p.category && set.add(p.category));
    return Array.from(set);
  }, [products]);

  return (
    <Card className="border-slate-200" data-testid="products-panel">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <Layers className="h-4 w-4 text-blue-600" /> Stax products
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Products selectable on a ticket. Also used on the Stax adoption checklist
          across accounts.
        </p>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <div className="grid grid-cols-12 gap-2 items-end p-3 border border-dashed border-slate-300 rounded-md bg-slate-50/40">
          <div className="col-span-5">
            <Label className="text-xs">Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. DNS Filtering"
              data-testid="new-product-name"
            />
          </div>
          <div className="col-span-3">
            <Label className="text-xs">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="new-product-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-3">
            <Label className="text-xs">Description</Label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional"
              data-testid="new-product-description"
            />
          </div>
          <div className="col-span-1 flex justify-end">
            <Button onClick={add} data-testid="add-product-btn" size="sm">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-xs text-slate-400 py-4">Loading…</div>
        ) : (
          <table className="w-full text-sm" data-testid="products-table">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="py-2 pl-1">Name</th>
                <th className="py-2 w-40">Category</th>
                <th className="py-2">Description</th>
                <th className="py-2 w-24">Archived</th>
                <th className="py-2 w-16 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} data-testid={`product-row-${p.id}`} className="border-b border-slate-50">
                  <td className="py-2 pl-1">
                    <Input
                      defaultValue={p.name}
                      onBlur={(e) => e.target.value !== p.name && update(p.id, { name: e.target.value })}
                      className="h-8"
                    />
                  </td>
                  <td className="py-2">
                    <Select value={p.category || "Custom"} onValueChange={(v) => update(p.id, { category: v })}>
                      <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-2">
                    <Input
                      defaultValue={p.description || ""}
                      onBlur={(e) => e.target.value !== (p.description || "") && update(p.id, { description: e.target.value || null })}
                      className="h-8"
                    />
                  </td>
                  <td className="py-2">
                    {p.archived ? <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">archived</span> : <span className="text-[11px] text-slate-400">—</span>}
                  </td>
                  <td className="py-2 text-right">
                    {!p.archived && (
                      <Button size="icon" variant="ghost" onClick={() => archive(p.id, p.name)} data-testid={`archive-product-${p.id}`}>
                        <Archive className="h-4 w-4 text-slate-400 hover:text-amber-600" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {products.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-xs text-slate-400">No products yet</td></tr>
              )}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
